// code-guardian-ignore-duplicates: parallel master-page shell: the StoreMasterPage tab strip drawn inline on purpose so every tabbed page matches
import { useSearchParams } from 'react-router-dom';
import { ClipboardCheck, ListChecks, Route } from 'lucide-react';
import { usePageLine } from '../hooks/usePageLine';
import VisitComplianceTab from '../components/store-visits/VisitComplianceTab';
import StoreVisitAnswersTab from '../components/store-visits/StoreVisitAnswersTab';
import PreJourneyPlanReportTab from '../components/store-visits/PreJourneyPlanReportTab';

/**
 * Reports › Store Visit Compliance (the page that was called Store Visit Tasks; same
 * address, same menu code).
 *
 * Three tabs: who met their weekly visit target, the questionnaire answers that used
 * to be the whole page, and the Pre-Journey Plan (each planned person's visits due and done). This shell supplies only the heading and the tab strip; each
 * tab keeps its own data, filters and download.
 */

type Tab = 'compliance' | 'answers' | 'plan';

const TABS: { key: Tab; label: string; icon: typeof ListChecks }[] = [
  { key: 'compliance', label: 'Compliance', icon: ListChecks },
  { key: 'answers', label: 'Questionnaire answers', icon: ClipboardCheck },
  { key: 'plan', label: 'Pre-Journey Plan', icon: Route },
];

export default function StoreVisitTasksReportPage() {
  const line = usePageLine();

  // The tab lives in the URL, so a link can point straight at either half and a
  // reload stays where the reader was. An unknown ?tab= falls back to Compliance.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const activeTab: Tab = TABS.some((t) => t.key === fromUrl) ? (fromUrl as Tab) : 'compliance';

  const selectTab = (tab: Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* min-w-0: lets the line be cut at the page's edge instead of widening it. */}
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-gray-900">Store Visit Compliance</h1>
        {line && <p className="mt-1 truncate text-sm text-gray-500" title={line}>{line}</p>}
      </div>

      {/* The StoreMasterPage strip. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mounted on demand: each tab loads its own report when it opens. */}
      {activeTab === 'compliance' && <VisitComplianceTab />}
      {activeTab === 'answers' && <StoreVisitAnswersTab />}
      {activeTab === 'plan' && <PreJourneyPlanReportTab />}
    </div>
  );
}
